import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private fun tourLaneKey(row: TourStageRow, phase: String) = "${if (phase == "qualification") row.roundNo ?: 0 else 0}:${row.lane ?: 0}"
private fun tourLaneLabel(row: TourStageRow, phase: String): String = if (row.lane == null) "Dráhy nezaznamenány" else
    (if (phase == "qualification") row.roundNo?.let { "$it. runda · " }.orEmpty() else "") + "Dráha ${row.lane}"

@Composable
internal fun TourPlayEditor(token: String, id: String, workflowPhase: String? = null, qualificationRoundsStarted: Boolean = true, onChanged: () -> Unit = {}, onAdvanced: (String) -> Unit = {}) {
    var bundle by remember { mutableStateOf<TourPlayBundle?>(null) }
    var tournament by remember { mutableStateOf<TourTournamentDto?>(null) }
    var phase by remember { mutableStateOf("") }
    var editing by remember { mutableStateOf<String?>(null) }
    var tie by remember { mutableStateOf<String?>(null) }
    var withdrawal by remember { mutableStateOf<TourStageRow?>(null) }
    var confirmation by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var busy by remember { mutableStateOf(false) }
    var retry by remember { mutableStateOf(0) }
    var selectedLane by remember { mutableStateOf("") }
    var laneInitialized by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    suspend fun reload() {
        tournament = TourOperationsApi.tournament(token, id)
        bundle = TourPlayApi.bundle(token, id)
        if (phase.isEmpty()) phase = workflowPhase ?: bundle!!.phase.takeUnless { it == "finished" } ?: "final"
    }
    LaunchedEffect(retry) {
        loading = true; error = null
        try { reload() } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    fun operate(action: suspend () -> Unit) {
        if (busy || loading) return
        scope.launch {
            busy = true; error = null
            try {
                action(); editing = null; tie = null; withdrawal = null; confirmation = null
                reload(); onChanged()
            } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { busy = false }
        }
    }
    if (loading || busy || error != null) Div(attrs = { classes("tour-admin-status") }) {
        Div(attrs = { attr("aria-live", "polite") }) {
            if (loading) P { Text("Načítám hry…") } else if (busy) P { Text("Ukládám…") }
            TourError(error)
        }
        if (error != null) Button(attrs = { if (loading || busy || editing != null) disabled(); onClick { retry++ } }) { Text("Načíst znovu") }
    }
    val current = bundle ?: return
    val t = tournament ?: return
    val disabled = busy || loading
    val formOpen = editing != null || tie != null || withdrawal != null || confirmation != null
    val canEdit = t.status in listOf("running", "finished") && current.phase != "finished" && (phase == current.phase || current.correctionMode)
    val rows = current.rows(phase)
    val groupedRows = rows.sortedWith(compareBy<TourStageRow> { if (phase == "qualification") it.roundNo ?: 0 else 0 }.thenBy { it.lane ?: 99 }
        .thenBy { if (it.lane == null) it.rank ?: Int.MAX_VALUE else it.position ?: it.seed ?: Int.MAX_VALUE }.thenBy { it.entryId })
        .groupBy { tourLaneKey(it, phase) }
    // Partial lanes still need a score sheet; withdrawn players need no further games.
    val unfinishedLanes = groupedRows.filterValues { players -> players.any { !it.complete && it.withdrawnPhase == null } }.keys
    val laneGroups = groupedRows.entries.sortedBy { it.key !in unfinishedLanes }
        .associate { it.key to it.value }
    val rounds = if (phase == "qualification") rows.mapNotNull { it.roundNo }.distinct().sorted() else emptyList()
    val filterOptions = listOf("pending" to "Všechny neodehrané dráhy") +
        rounds.map { "pending:$it" to "$it. runda · Neodehrané dráhy" } +
        listOf("" to "Všechny dráhy") + rounds.map { "round:$it" to "$it. runda · Všechny dráhy" }
    val laneOptions = filterOptions + laneGroups.map { (key, players) ->
        val state = when {
            key in unfinishedLanes -> " · Neodehráno"
            players.all { it.complete } -> " · Odehráno"
            else -> " · Uzavřeno"
        }
        key to (tourLaneLabel(players.first(), phase) + state)
    }
    LaunchedEffect(phase, laneGroups.keys.toList()) {
        if (workflowPhase != null && !laneInitialized && laneGroups.isNotEmpty()) { selectedLane = laneGroups.keys.first(); laneInitialized = true }
    }
    val effectiveLane = selectedLane.takeIf { value -> laneOptions.any { it.first == value } }.orEmpty()
    val visibleRows = laneGroups.filter { (lane, players) -> when {
        effectiveLane.isBlank() -> true
        effectiveLane == "pending" -> lane in unfinishedLanes
        effectiveLane.startsWith("pending:") -> lane in unfinishedLanes && players.first().roundNo?.toString() == effectiveLane.substringAfter(':')
        effectiveLane.startsWith("round:") -> players.first().roundNo?.toString() == effectiveLane.substringAfter(':')
        else -> lane == effectiveLane
    } }.values.flatten()
    Div(attrs = { classes("card") }) {
        H2 { Text(if (workflowPhase == null) t.title else tourLabel(phase, TOUR_PHASES)) }
        P { Text("${tourLabel(t.discipline, TOUR_DISCIPLINES)} · " + if (current.phase == "finished") "Výsledky zveřejněny" else tourLabel(current.phase, TOUR_PHASES)) }
        if (t.status !in listOf("running", "finished")) P { Text("Nejprve zahajte kvalifikační rundu v kroku Prezentace.") }
        if (current.correctionMode) P { Text("Probíhá oprava. Skuteční postupující a dráhy zůstávají zachované. Po opravě znovu potvrďte a zveřejněte výsledky.") }
        if (workflowPhase == null) TourChoice("Zobrazená fáze", phase, TOUR_PHASES, disabled || formOpen) { phase = it; selectedLane = "" }
        if (rows.isNotEmpty()) Div(attrs = { classes("tour-admin-toolbar") }) {
            TourChoice("Dráha pro zápis", effectiveLane, laneOptions, disabled || formOpen) { selectedLane = it }
            val lanes = laneGroups.keys.toList()
            val index = lanes.indexOf(effectiveLane)
            if (lanes.size > 1) {
                Button(attrs = { if (disabled || formOpen || index <= 0) disabled(); onClick { selectedLane = lanes[index - 1] } }) { Text("← Předchozí dráha") }
                Button(attrs = { if (disabled || formOpen || index < 0 || index >= lanes.lastIndex) disabled(); onClick { selectedLane = lanes[index + 1] } }) { Text("Další dráha →") }
            }
        }
        P(attrs = { classes("muted") }) { Text(if (phase == "final") "Finále přičítá jednu hru ke dvěma semifinálovým. Zobrazeny jsou všechny tři hry." else "Zapisujte čisté náhozy 0–300. Prázdné pole znamená neodehranou hru. Handicap se přičítá ke každé odehrané hře.") }
        visibleRows.groupBy { tourLaneKey(it, phase) }.forEach { (lane, players) -> key(phase, lane) {
            TourLaneScoreTable(players, current.awards, phase, current.revision, tourLaneLabel(players.first(), phase),
                canEdit, disabled || tie != null || withdrawal != null || confirmation != null || (editing != null && editing != lane),
                onDirty = { editing = if (it) lane else null },
                onSave = { scores -> operate { TourPlayApi.lane(token, id, current.revision, phase, scores) } },
                onWithdraw = { withdrawal = it })
        } }
        if (rows.isEmpty()) P { Text(if (phase == "qualification") "Zatím není zahájená žádná obsazená runda." else "Postupující do této fáze zatím nebyli potvrzeni.") }
        else if (visibleRows.isEmpty()) P(attrs = { classes("muted") }) { Text("Ve vybraném rozsahu nejsou žádné neodehrané dráhy.") }
        rows.filter { it.tiePending }.groupBy { it.tieKey }.forEach { (key, group) ->
            P { Text("Úplná shoda: ${group.joinToString { it.playerName }}") }
            if (canEdit) Button(attrs = { if (disabled || formOpen) disabled(); onClick { tie = key } }) { Text("Rozhodnout shodu: ${group.joinToString { it.playerName }}") }
        }
        if (current.phase != "qualification" && !current.correctionMode) Button(attrs = {
            if (disabled || formOpen) disabled(); onClick { confirmation = "correction" }
        }) { Text("Otevřít opravu výsledků") }
    }
    tie?.let { key -> key(key, phase) {
        TourTieForm(rows.filter { it.tieKey == key }, disabled, onCancel = { tie = null }) { entries, reason ->
            operate { TourPlayApi.tie(token, id, current.revision, phase, entries, reason) }
        }
    } }
    withdrawal?.let { row -> key(row.entryId) {
        TourReasonForm(if (row.withdrawnPhase == null) "Odstoupení: ${row.playerName}" else "Zrušit odstoupení: ${row.playerName}",
            "Odehrané hry zůstanou zachované. Postupové místo se nedoplňuje. Pro základní body se použije poslední místo účastníka.",
            disabled, onCancel = { withdrawal = null }) { reason ->
            operate { TourPlayApi.withdraw(token, id, current.revision, row.entryId, if (row.withdrawnPhase == null) phase else null, reason) }
        }
    } }
    if (current.phase in listOf("qualification", "semifinal") && t.status == "running" && (workflowPhase == null || workflowPhase == current.phase)) Div(attrs = { classes("card") }) {
        val next = if (current.phase == "qualification") "semifinal" else "final"
        val limit = if (next == "semifinal") 24 else 15
        val source = current.rows(current.phase)
        val phaseComplete = source.isNotEmpty() && source.all { row -> row.complete || row.withdrawnPhase?.let { withdrawn ->
            TOUR_PHASES.indexOfFirst { it.first == withdrawn } <= TOUR_PHASES.indexOfFirst { it.first == current.phase }
        } == true } && (current.phase != "qualification" || qualificationRoundsStarted)
        val tiesPending = source.any { it.tiePending }
        val advancing = source.filter { it.rank != null && it.rank <= limit && it.withdrawnPhase == null }
        H2 { Text("Průběžné výsledky fáze") }
        P { Text("${tourLabel(current.phase, TOUR_PHASES)} · Postup do ${if (next == "semifinal") "semifinále" else "finále"}: nejvýše $limit hráčů. Nasazení od dráhy 1 po třech podle pořadí; po odstoupení zůstávají místa volná.") }
        if (source.isNotEmpty()) {
            val advancingIds = advancing.map { it.entryId }.toSet()
            val nonAdvancing = source.filter { it.entryId !in advancingIds }
            Div(attrs = { classes("table-scroll") }) {
                Table(attrs = { classes("public-table", "tour-score-table", "tour-advancement-table"); attr("aria-label", "Soupiska pro ${tourLabel(next, TOUR_PHASES)}") }) {
                    Thead { Tr { listOf("Pořadí", "Hráč", "Součet s HDCP", "Bez HDCP", "Dráha / pozice", "Stav").forEach { Th { Text(it) } } } }
                    listOf(advancing, nonAdvancing).forEachIndexed { group, players -> Tbody {
                        players.sortedWith(compareBy<TourStageRow> { it.rank ?: Int.MAX_VALUE }.thenBy(tourNameComparator) { it.playerName }).forEachIndexed { index, row ->
                            Tr(attrs = { if (group == 1 && index == 0) classes("tour-advancement-cut") }) {
                                Td { Text(row.rank?.toString() ?: if (row.tiePending) "Shoda" else "—") }
                                Td(attrs = { classes("tour-player-name") }) { Text(row.playerName) }
                                Td { B { Text(row.adjustedTotal?.toString() ?: "—") } }
                                Td { Text(row.rawTotal?.toString() ?: "—") }
                                Td { Text(if (group == 0 && phaseComplete && !tiesPending) "${(row.rank!!-1)/3+1} / ${(row.rank-1)%3+1}" else "—") }
                                Td { Text(when {
                                    group == 0 -> if (phaseComplete && !tiesPending) "Postupuje" else "Průběžně postupuje"
                                    row.withdrawnPhase != null -> "Odstoupil"
                                    row.tiePending -> "Nerozhodnutá shoda"
                                    !row.complete -> if (row.played > 0) "Rozehráno" else "Čeká na hry"
                                    else -> if (phaseComplete && !tiesPending) "Nepostupuje" else "Průběžně nepostupuje"
                                }) }
                            }
                        }
                    } }
                }
            }
        } else P { Text("Zatím nejsou dostupné výsledky této fáze.") }
        if (phaseComplete) {
            if (tiesPending) P(attrs = { classes("muted") }) { Text("Před potvrzením postupujících rozhodněte všechny úplné shody.") }
            P { Text("Potvrzením uzamknete tuto fázi. Její pozdější oprava nezmění skutečné postupující.") }
            Button(attrs = { classes("primary"); if (disabled || formOpen || tiesPending) disabled(); onClick { operate { TourPlayApi.advance(token, id, current.revision); phase = ""; onChanged(); onAdvanced(next) } } }) { Text("Potvrdit postup a nasazení") }
        } else P(attrs = { classes("muted") }) { Text("Potvrzení postupujících bude dostupné po dokončení všech hráčů a obsazených rund této fáze. Odstoupení označte u hráče.") }
    }
    if (current.phase in listOf("final", "finished") && (workflowPhase == null || workflowPhase == "final")) Div(attrs = { classes("card") }) {
        H2 { Text(if (current.phase == "finished") "Konečné výsledky" else "Náhled konečného pořadí") }
        TourResultTable(current)
        if (current.phase == "final") {
            if (confirmation == "publish") {
                P { Text("Zveřejnit konečné pořadí a uzavřít turnaj? Server zkontroluje úplnost všech fází a rozhodnutí shod.") }
                Button(attrs = { classes("primary"); if (disabled) disabled(); onClick { operate { TourPlayApi.publish(token, id, current.revision) } } }) { Text("Potvrdit zveřejnění výsledků") }
                Button(attrs = { if (busy) disabled(); onClick { confirmation = null } }) { Text("Zrušit zveřejnění") }
            } else Button(attrs = { classes("primary"); if (disabled || formOpen) disabled(); onClick { confirmation = "publish" } }) { Text("Zveřejnit a uzavřít turnaj") }
        }
    }
    if (confirmation == "correction") TourReasonForm("Oprava výsledků", "Oprava zachová skutečné postupující, jejich dráhy i poslední zveřejněnou verzi. Upravené výsledky je potřeba znovu zveřejnit.",
        disabled, onCancel = { confirmation = null }) { reason -> operate { TourPlayApi.correction(token, id, current.revision, reason) } }
    TourLegacyAwardEditor(current, disabled || formOpen) { award, counts ->
        operate { TourPlayApi.splitAward(token, id, current.revision, award, counts[0], counts[1], counts[2]) }
    }
}

@Composable
private fun TourReasonForm(title: String, explanation: String, busy: Boolean, onCancel: () -> Unit, onConfirm: (String) -> Unit) {
    var reason by remember(title) { mutableStateOf("") }
    LaunchedEffect(title) { document.getElementById("tour-play-action")?.asDynamic()?.scrollIntoView() }
    Div(attrs = { classes("card", "tour-play-action"); id("tour-play-action") }) {
        H2 { Text(title) }; P { Text(explanation) }
        Form(attrs = { classes("tour-form"); onSubmit { it.preventDefault(); if (!busy && reason.isNotBlank()) onConfirm(reason.trim()) } }) {
            TourField("Důvod rozhodnutí", reason, busy) { reason = it }
            Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || reason.isBlank()) disabled() }) { Text("Potvrdit rozhodnutí") }
            Button(attrs = { type(ButtonType.Button); if (busy) disabled(); onClick { onCancel() } }) { Text("Zrušit rozhodnutí") }
        }
    }
}

@Composable
private fun TourTieForm(rows: List<TourStageRow>, busy: Boolean, onCancel: () -> Unit, onConfirm: (List<String>, String) -> Unit) {
    var choices by remember { mutableStateOf(rows.associate { it.entryId to "" }) }
    var reason by remember { mutableStateOf("") }
    val valid = choices.values.none { it.isBlank() } && choices.values.toSet().size == rows.size
    LaunchedEffect(Unit) { document.getElementById("tour-play-action")?.asDynamic()?.scrollIntoView() }
    Div(attrs = { classes("card", "tour-play-action"); id("tour-play-action") }) {
        H2 { Text("Rozhodnutí úplné shody") }
        P { Text("Všechna herní kritéria jsou stejná. Zadejte konečné pořadí všech hráčů této shody a způsob rozhodnutí.") }
        Form(attrs = { classes("tour-form"); onSubmit { it.preventDefault(); if (!busy && valid && reason.isNotBlank()) onConfirm(rows.sortedBy { choices[it.entryId]!!.toInt() }.map { it.entryId }, reason.trim()) } }) {
            rows.forEach { row -> TourChoice("Pořadí ve shodě: ${row.playerName}", choices[row.entryId] ?: "", listOf("" to "Vyberte pořadí") + rows.indices.map { (it+1).toString() to "${it+1}." }, busy) { choices = choices + (row.entryId to it) } }
            TourField("Způsob rozhodnutí shody", reason, busy) { reason = it }
            Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || !valid || reason.isBlank()) disabled() }) { Text("Uložit rozhodnutí shody") }
            Button(attrs = { type(ButtonType.Button); if (busy) disabled(); onClick { onCancel() } }) { Text("Zrušit rozhodnutí shody") }
        }
    }
}

@Composable
private fun TourResultTable(bundle: TourPlayBundle) {
    val results = bundle.preview
    val qualification = bundle.qualification.associateBy { it.entryId }
    val semifinal = bundle.semifinal.associateBy { it.entryId }
    val final = bundle.final.associateBy { it.entryId }
    if (results.isEmpty()) P { Text("Zatím nejsou odehrané výsledky.") }
    else Div(attrs = { classes("table-scroll") }) {
        Table(attrs = { classes("public-table", "tour-score-table"); attr("aria-label", "Konečné pořadí Tour") }) {
            Thead { Tr { listOf("Pořadí", "Hráč", "Nához s HDCP", "Kvalifikace", "Stav", "Základ + kvalifikace + pódium", "Body").forEach { Th { Text(it) } } } }
            Tbody { results.forEach { row ->
                val finalRow = final[row.entryId]
                val semifinalRow = semifinal[row.entryId]
                val lastStage = finalRow ?: semifinalRow ?: qualification[row.entryId]
                val lastPhase = when {
                    finalRow != null -> "final"
                    semifinalRow != null -> "semifinal"
                    else -> "qualification"
                }
                val status = when {
                    row.withdrawnPhase != null -> "${tourLabel(row.withdrawnPhase, TOUR_PHASES)} · Odstoupil"
                    row.pending -> "${tourLabel(lastPhase, TOUR_PHASES)} · Nerozhodnutá shoda"
                    row.dnf -> "Kvalifikace · Nedokončil"
                    finalRow != null && finalRow.game3 == null -> "Finále · Čeká na hru"
                    finalRow != null -> if (finalRow.complete) "Finále · Dohráno" else "Finále · Rozehráno"
                    else -> tourLabel(lastPhase, TOUR_PHASES)
                }
                Tr(attrs = { if (row.joker) { classes("tour-joker-row"); title("Použitý Joker ×2") } }) {
                    Td { Text(row.rank?.toString() ?: "—") }
                    Td(attrs = { classes("tour-player-name") }) { Text(row.playerName) }
                    Td { B { Text(lastStage?.adjustedTotal?.toString() ?: "—") } }
                    Td { Text(row.qualificationRank?.toString() ?: "—") }
                    Td { Text(status) }
                    Td { Text(row.basePoints?.let { "$it${if (row.joker) " × 2" else ""} + ${row.qualificationBonus ?: 0} + ${row.podiumBonus ?: 0}" } ?: "—") }
                    Td { B { Text(row.points?.toString() ?: "—") } }
                }
            } }
        }
    }
}
